import React, { useState, useRef, useEffect } from 'react';
import { PendingOrder } from '@shared/types';
import { calculatePnL, roundToTick } from '../config/instruments';
import { useMarketStore } from '../store/marketStore';
// Reuse the live order line styling so the draft is pixel-consistent with the
// real order line it becomes once confirmed.
import './LiveOrderLine.css';
import './PendingOrderLine.css';

export interface PendingOrderLineProps {
  order: PendingOrder;
  position: { avgPrice: number; quantity: number; symbol: string } | null;
  yPosition: number;
  horizontalOffset?: number;
  onDragPrice: (newPrice: number) => void;
  onCoordinateToPrice: (yCoordinate: number) => number | null;
  onUpdateOrder: (orderId: string, newPrice: number) => void;
  // Confirm / cancel the draft (rendered inside the pill, adjacent to content).
  onConfirm?: () => void;
  onCancel?: () => void;
  submitting?: boolean;
}

const PendingOrderLine: React.FC<PendingOrderLineProps> = ({
  order,
  position,
  yPosition,
  horizontalOffset = 0,
  onDragPrice,
  onCoordinateToPrice,
  onUpdateOrder,
  onConfirm,
  onCancel,
  submitting = false
}) => {
  const chartSymbol = useMarketStore(state => state.symbol);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPrice, setDragPrice] = useState<number>(order.price);
  const dragStartY = useRef<number>(0);

  // Color by ACTION (buy/sell) to match LiveOrderLine. The draft becomes this
  // exact order when confirmed, so it must look like that order, not invent
  // its own TP=green / SL=red scheme.
  const isBuy = order.type.includes('BUY');
  const isStop = order.type.includes('STOP');
  // Get symbol for price snapping - order.symbol, position.symbol, or chartSymbol
  const symbolForSnap = order.symbol || position?.symbol || chartSymbol;

  // Calculate profit/loss if this is TP/SL for a position using centralized config
  const calculateProfitLoss = (exitPrice: number) => {
    if (!position) return null;
    return calculatePnL(position.avgPrice, exitPrice, position.quantity, position.symbol);
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't start drag if clicking on a button
    if ((e.target as HTMLElement).tagName === 'BUTTON') {
      return;
    }

    e.stopPropagation();
    // Don't preventDefault on touch events initially to allow scrolling detection
    if (e.type !== 'touchstart') {
      e.preventDefault();
    }
    setIsDragging(true);
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartY.current = clientY;
    setDragPrice(order.price);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      // Get chart container to calculate relative Y position
      const chartContainer = document.querySelector('.chart-container');
      if (!chartContainer) return;

      const rect = chartContainer.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;

      // Use the callback to convert Y coordinate to price
      const newPrice = onCoordinateToPrice(relativeY);

      if (newPrice !== null) {
        // Snap to tick size
        const snappedPrice = roundToTick(newPrice, symbolForSnap);

        setDragPrice(snappedPrice);
        onDragPrice(snappedPrice);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      // Prevent scrolling while dragging
      e.preventDefault();

      const chartContainer = document.querySelector('.chart-container');
      if (!chartContainer) return;

      const rect = chartContainer.getBoundingClientRect();
      const touch = e.touches[0];
      const relativeY = touch.clientY - rect.top;

      const newPrice = onCoordinateToPrice(relativeY);

      if (newPrice !== null) {
        const snappedPrice = roundToTick(newPrice, symbolForSnap);
        setDragPrice(snappedPrice);
        onDragPrice(snappedPrice);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        // Update the order with new price
        onUpdateOrder(order.id, dragPrice);
      }

      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      if (isDragging) {
        onUpdateOrder(order.id, dragPrice);
      }
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragPrice, order.id, onDragPrice, onCoordinateToPrice, onUpdateOrder]);

  const displayPrice = isDragging ? dragPrice : order.price;
  const profitLoss = calculateProfitLoss(displayPrice);

  // The draft is always shown in the `pending` state (dashed / translucent /
  // pulse) to signal it hasn't been placed yet.
  return (
    <>
      {/* Horizontal line across the chart — same classes as the live order line */}
      <div
        className={`live-order-line ${isBuy ? 'buy' : 'sell'} ${isStop ? 'stop' : 'limit'} ${isDragging ? 'dragging' : ''} pending`}
        style={{
          top: `${yPosition}px`,
          right: `${80 + horizontalOffset}px`,
        }}
      />
      {/* Control pill — same classes/content/order as the live order line pill */}
      <div
        className={`live-order-line-controls ${isDragging ? 'dragging' : ''} ${isBuy ? 'buy' : 'sell'} ${isStop ? 'stop' : 'limit'} pending`}
        style={{
          top: `${yPosition}px`,
          right: `${80 + horizontalOffset}px`,
          transform: 'translateY(-50%)',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <span className="live-order-badge order">ORD</span>
        <span className="live-order-subtype">{isStop ? 'STP' : 'LMT'}</span>
        <span className="live-order-price">{displayPrice.toFixed(2)}</span>
        <span className="live-order-quantity">x{order.quantity}</span>
        {profitLoss && (
          <span className={`live-order-pnl ${profitLoss.dollars >= 0 ? 'profit' : 'loss'}`}>
            {profitLoss.dollars >= 0 ? '+' : ''}{profitLoss.dollars.toFixed(2)}
          </span>
        )}
        {/* Confirm / cancel — adjacent to the pill content, matching the line+pill look */}
        {(onConfirm || onCancel) && (
          <span className="draft-pill-actions">
            {onConfirm && (
              <button
                className="draft-pill-confirm"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onConfirm(); }}
                disabled={submitting}
                title={`Confirm @ ${displayPrice.toFixed(2)}`}
              >
                ✓
              </button>
            )}
            {onCancel && (
              <button
                className="draft-pill-cancel"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onCancel(); }}
                disabled={submitting}
                title="Cancel draft"
              >
                ✕
              </button>
            )}
          </span>
        )}
      </div>
    </>
  );
};

export default PendingOrderLine;
